import { CaretRightIcon, TrayIcon } from '@phosphor-icons/react'
import {
  AlertBanner,
  Button,
  EmptyState,
  EmptyStateDescription,
  EmptyStateIcon,
  EmptyStateTitle,
  Spinner,
} from '@curri/components'
import { useState, type CSSProperties, type ReactNode } from 'react'

import type { Resource } from '../../api/client.ts'
import type { AttemptStatusFilter } from '../../api/webhooks.ts'

import { describeFailure } from './format.ts'
import { Panel, PANEL_HAIRLINE } from './Panel.tsx'
import { SegmentedControl } from './SegmentedControl.tsx'

// The history table both webhook pipelines are read through: the filters, the
// paged grid, and the row that opens into a detail pane. The caller supplies
// its columns, its cells and its detail — everything that differs between a
// delivery attempt and a payload attempt — and nothing else.
//
// The table is the design's grid rather than @curri/components' <Table> — the
// BUILD look (a floating header bar, rounded hover rows, a two-pane detail) is
// not something the table component's slots express. ARIA table roles keep the
// semantics the real <table> had.

const FILTERS: { label: string; value: AttemptStatusFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Delivered', value: 'delivered' },
  { label: 'Failed', value: 'failed' },
]

// Just 7 for now — 30 and 90 are the windows that make the dashboard's reads
// scan every partner's attempts rather than the caller's. See MAX_WINDOW_DAYS in
// the service's surfaces/rest/webhooks.ts. The control hides itself while there
// is only one choice, and comes back on its own when this list grows again.
export const WINDOW_OPTIONS = [7]

export interface AttemptColumn {
  header: string
  width: string
}

// The chevron column belongs to the frame, so a caller describes only the
// columns holding its own data.
const gridFor = (columns: AttemptColumn[]): CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: ['30px', ...columns.map(column => column.width)].join(' '),
  gap: 12,
  alignItems: 'center',
})

/** A label/value line in a detail pane, as both attempt tables draw them. */
export const MetaRow = ({
  color,
  label,
  value,
}: {
  color?: string
  label: string
  value: ReactNode
}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'baseline',
      gap: 12,
      padding: '9px 0',
      borderBottom: `1px solid ${PANEL_HAIRLINE}`,
    }}
  >
    <span
      style={{
        flex: 'none',
        width: 74,
        fontSize: 12.5,
        color: 'var(--curri-text-tertiary)',
      }}
    >
      {label}
    </span>
    <span
      style={{
        flex: 1,
        minWidth: 0,
        fontFamily: 'var(--curri-font-mono)',
        fontSize: 11.5,
        color: color ?? 'var(--curri-text-secondary)',
        overflowWrap: 'anywhere',
      }}
    >
      {value}
    </span>
  </div>
)

// Coloured by status class rather than by `success`: the cell reports what the
// endpoint *said*, and the pill beside it already carries the outcome.
const statusColor = (httpStatus: number): string => {
  if (httpStatus < 300) return 'var(--color-text-success)'
  if (httpStatus >= 400) return 'var(--color-text-danger)'
  return 'var(--curri-text)'
}

/**
 * The response column, shared by both attempt cards.
 *
 * `httpStatus` is null when the request never got a reply — a timeout, a refused
 * connection — which is an outcome rather than missing data, so it is spelled out
 * instead of rendered as `0` or an empty cell.
 *
 * `statusText` is the reason phrase ("OK", "Internal Server Error"), which only
 * the delivery pipeline records. It rides in the hover tooltip so the cell never
 * wraps, and is simply absent for payload attempts.
 */
export const ResponseCell = ({
  httpStatus,
  statusText = null,
}: {
  httpStatus: number | null
  statusText?: string | null
}) => {
  if (httpStatus === null) {
    return (
      <span
        style={{ color: 'var(--curri-text-tertiary)', whiteSpace: 'nowrap' }}
        title={statusText ?? undefined}
      >
        No response
      </span>
    )
  }

  return (
    <span
      style={{
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        color: statusColor(httpStatus),
      }}
      title={statusText ?? undefined}
    >
      {httpStatus}
    </span>
  )
}

const HeaderRow = ({ columns }: { columns: AttemptColumn[] }) => (
  <div
    role="row"
    style={{
      ...gridFor(columns),
      marginTop: 18,
      padding: '9px 12px',
      background: 'var(--curri-canvas)',
      borderRadius: 8,
      fontSize: 10.5,
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'var(--curri-text-tertiary)',
    }}
  >
    <span aria-label="Expand" role="columnheader" />
    {columns.map((column, index) => (
      <span
        key={column.header}
        role="columnheader"
        // The response the endpoint gave closes every one of these tables, and
        // the numbers read straight against the panel's right edge.
        style={index === columns.length - 1 ? { textAlign: 'right' } : undefined}
      >
        {column.header}
      </span>
    ))}
  </div>
)

const AttemptRow = ({
  cells,
  columns,
  detail,
  isExpanded,
  name,
  onToggle,
}: {
  cells: ReactNode
  columns: AttemptColumn[]
  detail: ReactNode
  isExpanded: boolean
  name: string | null
  onToggle: () => void
}) => (
  <div role="rowgroup" style={{ borderBottom: `1px solid ${PANEL_HAIRLINE}` }}>
    <div
      className="webhook-row"
      onClick={onToggle}
      role="row"
      style={{
        ...gridFor(columns),
        padding: '11px 12px',
        cursor: 'pointer',
      }}
    >
      <span
        role="cell"
        style={{
          display: 'inline-flex',
          color: 'var(--curri-text-tertiary)',
        }}
      >
        {/* A real button, so the expander is reachable and operable by
            keyboard. It carries no handler of its own: Enter/Space on a
            native button synthesize a click that bubbles to the row's
            onClick, so pointer and keyboard share the one toggle path. */}
        <button
          aria-expanded={isExpanded}
          aria-label={`${isExpanded ? 'Hide' : 'Show'} attempt details${
            name ? ` for ${name}` : ''
          }`}
          className="webhook-expand"
          type="button"
        >
          {/* Rotate the icon itself: the cell is a 30px grid column, so a
              transform on it would swing the chevron around the column's
              centre rather than spinning it in place. */}
          <CaretRightIcon
            className="webhook-chevron"
            size={16}
            style={{ transform: isExpanded ? 'rotate(90deg)' : undefined }}
          />
        </button>
      </span>
      {cells}
    </div>
    {isExpanded && (
      <div className="webhook-open" role="row">
        <div aria-colspan={columns.length + 1} role="cell">
          {detail}
        </div>
      </div>
    )}
  </div>
)

const NoAttempts = ({ body, title }: { body: ReactNode; title: string }) => (
  <EmptyState size="small">
    <EmptyStateIcon>
      <TrayIcon />
    </EmptyStateIcon>
    <EmptyStateTitle>{title}</EmptyStateTitle>
    <EmptyStateDescription>{body}</EmptyStateDescription>
  </EmptyState>
)

export const AttemptsFrame = <T,>({
  columns,
  description,
  emptyBody,
  emptyTitle,
  filters,
  isLoadingMore,
  keyOf,
  loadMore,
  loadMoreFailed,
  nextCursor,
  onStatusChange,
  onWindowChange,
  renderDetail,
  renderRow,
  resource,
  rowName,
  status,
  title,
  windowDays,
  windowOptions,
}: {
  columns: AttemptColumn[]
  description: ReactNode
  emptyBody: ReactNode
  emptyTitle: string
  /** Controls a caller adds ahead of the shared outcome and window filters. */
  filters?: ReactNode
  isLoadingMore: boolean
  keyOf: (row: T) => number
  loadMore: () => void
  loadMoreFailed: boolean
  nextCursor: number | null
  onStatusChange: (next: AttemptStatusFilter) => void
  onWindowChange: (next: number) => void
  renderDetail: (row: T) => ReactNode
  renderRow: (row: T, isExpanded: boolean) => ReactNode
  resource: Resource<T[]>
  /** Names the row in the expander's accessible label. */
  rowName: (row: T) => string | null
  status: AttemptStatusFilter
  title: string
  windowDays: number
  windowOptions: number[]
}) => {
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set())

  const toggle = (key: number) =>
    setExpanded(current => {
      const next = new Set(current)
      if (!next.delete(key)) next.add(key)
      return next
    })

  return (
    <Panel style={{ paddingTop: 34, paddingBottom: 32 }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '12px 16px',
        }}
      >
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <Panel.Title meta="newest first">{title}</Panel.Title>
          <Panel.Description>{description}</Panel.Description>
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            gap: 12,
          }}
        >
          {filters}
          <SegmentedControl
            ariaLabel="Filter by outcome"
            onChange={onStatusChange}
            options={FILTERS}
            value={status}
          />
          {windowOptions.length > 1 && (
            <SegmentedControl
              ariaLabel="Time window"
              onChange={onWindowChange}
              options={windowOptions.map(days => ({
                label: `${days} days`,
                value: days,
              }))}
              value={windowDays}
            />
          )}
        </div>
      </div>

      {resource.status === 'loading' && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <Spinner size="lg" />
        </div>
      )}

      {resource.status === 'failed' &&
        (() => {
          const copy = describeFailure(resource.failure)
          return (
            <div style={{ marginTop: 16 }}>
              <AlertBanner title={copy.title} type={copy.tone}>
                {copy.body}
              </AlertBanner>
            </div>
          )
        })()}

      {resource.status === 'ready' &&
        (resource.data.length === 0 ? (
          <div style={{ marginTop: 16 }}>
            <NoAttempts body={emptyBody} title={emptyTitle} />
          </div>
        ) : (
          <div aria-label="Webhook attempts" role="table">
            <HeaderRow columns={columns} />
            {resource.data.map(row => {
              const isExpanded = expanded.has(keyOf(row))
              return (
                <AttemptRow
                  cells={renderRow(row, isExpanded)}
                  columns={columns}
                  detail={isExpanded ? renderDetail(row) : null}
                  isExpanded={isExpanded}
                  key={keyOf(row)}
                  name={rowName(row)}
                  onToggle={() => toggle(keyOf(row))}
                />
              )
            })}
          </div>
        ))}

      {loadMoreFailed && (
        <div style={{ marginTop: 12 }}>
          <AlertBanner type="danger">
            Could not load more attempts. The ones already shown are unaffected.
          </AlertBanner>
        </div>
      )}

      {resource.status === 'ready' && resource.data.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginTop: 16,
          }}
        >
          <span
            style={{
              fontFamily: 'var(--curri-font-mono)',
              fontSize: 11.5,
              color: 'var(--curri-text-tertiary)',
            }}
          >
            Showing {resource.data.length}
            {nextCursor === null ? ' — that is everything in this window' : ''}
          </span>
          {nextCursor !== null && (
            <Button
              label={isLoadingMore ? 'Loading…' : 'Load more'}
              loading={isLoadingMore}
              onClick={loadMore}
              size="small"
              variant="secondary"
            />
          )}
        </div>
      )}
    </Panel>
  )
}
